import {
  ANY_OF_THOSE,
  DOES_NOT_EXISTS,
  EXISTS,
  IS_BETWEEN,
  IS_GREATER,
  IS_LESS,
  ONLY_TEXT,
} from '../../constants/filter';
import { NL_LANES } from './constants';
import { resolveVendorFilterOption } from './resolveVendorFilterOptions';

const FCO_DATE_PAIR_KEYS = {
  dueFrom: { field: 'dueDate', bound: 'from' },
  dueTo: { field: 'dueDate', bound: 'to' },
  postedFrom: { field: 'postedDate', bound: 'from' },
  postedTo: { field: 'postedDate', bound: 'to' },
  modifiedFrom: { field: 'modifiedDate', bound: 'from' },
  modifiedTo: { field: 'modifiedDate', bound: 'to' },
};

const FCO_MULTI_SELECT_KEYS = [
  'state',
  'setAside',
  'type',
  'keywords',
  'naicsCategory',
  'pscCategory',
  'department',
  'agency',
  'office',
  'country',
  'zip',
  'federalAgent',
];

const FCO_CHIP_LABELS = {
  state: 'State',
  setAside: 'Set-aside',
  type: 'Type',
  keywords: 'Keyword',
  naicsCategory: 'NAICS',
  pscCategory: 'PSC',
  department: 'Department',
  agency: 'Agency',
  office: 'Office',
  country: 'Country',
  zip: 'Zip',
  federalAgent: 'Federal agent',
  dueDate: 'Due date',
  postedDate: 'Posted date',
  modifiedDate: 'Modified date',
  text: 'Keyword',
  hasSetAside: 'Set-aside',
};

const VENDOR_DATE_PAIR_KEYS = {
  registrationFrom: { field: 'registrationDate', bound: 'from' },
  registrationTo: { field: 'registrationDate', bound: 'to' },
  expirationFrom: { field: 'expirationDate', bound: 'from' },
  expirationTo: { field: 'expirationDate', bound: 'to' },
};

// Note: `country` is intentionally excluded. The vendors country filter is a gated
// dropdown (availableWhen/getCountriesList); applying a raw NL country value breaks the
// dependent state/zip availability logic, so any country value falls through to unmapped.
const VENDOR_MULTI_SELECT_KEYS = [
  'naicsCategory',
  'pscCategory',
  'state',
  'zipCode',
  'setAside',
  'sbaCertification',
  'structure',
  'keywords',
  'ueiCode',
  'cageCode',
];

const VENDOR_CHIP_LABELS = {
  naicsCategory: 'NAICS',
  pscCategory: 'PSC',
  state: 'State',
  zipCode: 'Zip',
  setAside: 'Set-aside',
  sbaCertification: 'SBA certification',
  structure: 'Entity structure',
  keywords: 'Keyword',
  ueiCode: 'UEI',
  cageCode: 'CAGE code',
  registrationDate: 'Registration date',
  expirationDate: 'Expiration date',
  text: 'Keyword',
  hasSetAside: 'Set-aside',
};

// Awards date group: awardDate, completionDate, ultimateCompletionDate, lastUpdated.
const AWARDS_DATE_PAIR_KEYS = {
  awardFrom: { field: 'awardDate', bound: 'from' },
  awardTo: { field: 'awardDate', bound: 'to' },
  completionFrom: { field: 'completionDate', bound: 'from' },
  completionTo: { field: 'completionDate', bound: 'to' },
  ultimateCompletionFrom: { field: 'ultimateCompletionDate', bound: 'from' },
  ultimateCompletionTo: { field: 'ultimateCompletionDate', bound: 'to' },
  modifiedFrom: { field: 'lastUpdated', bound: 'from' },
  modifiedTo: { field: 'lastUpdated', bound: 'to' },
};

// STRING_SCHEMA dropdowns are array-backed ANY_OF_THOSE multi-selects; the gateway emits
// these as arrays (or resolves naicsCategory/pscCategory/federalAgency/pricingType via
// `lookups`, which mergeLookupsIntoFilters folds in for keys listed here). All fit the
// shared multi-select path. Deliberately excluded: vendorName/ueiCode/cageCode (SINGLE_VALUE
// TEXT), title (PARTIAL {text,type}), potentialValue (bare number with no over/under operator
// from the service), and onlyDod (static boolean quick-filter) — all fall through to unmapped.
const AWARDS_MULTI_SELECT_KEYS = [
  'naicsCategory',
  'pscCategory',
  'setAside',
  'state',
  'extentCompeted',
  'federalAgency',
  'pricingType',
  'keywords',
];

// The interpret service sends award value as a bare amount (e.g. dollarsObligated: '1000000')
// meaning "over $X"; map it to a single-value IS_GREATER NUMBER filter. `potentialValue` is
// not currently emitted by the service, so it is left unmapped for now.
const AWARDS_NUMBER_SINGLE_KEYS = ['dollarsObligated'];

const AWARDS_CHIP_LABELS = {
  naicsCategory: 'NAICS',
  pscCategory: 'PSC',
  setAside: 'Set-aside',
  state: 'State',
  extentCompeted: 'Extent competed',
  federalAgency: 'Agency',
  pricingType: 'Pricing type',
  keywords: 'Keyword',
  awardDate: 'Award date',
  completionDate: 'Completion date',
  ultimateCompletionDate: 'Ultimate completion date',
  lastUpdated: 'Last updated',
  dollarsObligated: 'Award value',
  text: 'Keyword',
  hasSetAside: 'Set-aside',
};

// SLED (state & local) Contract Awards. The screen has only three filters: state (multi-select),
// agencyName (multi-select), and awardValue (AMOUNT_RANGE). The gateway emits `state` as an
// array, award value as `minValue` ("over $X") and/or `maxValue` ("under $X"), and `awardeeName`
// for the recipient/vendor. `awardeeName` is intentionally NOT mapped: it is the award
// recipient, semantically distinct from the screen's `agencyName` (the awarding agency), and
// the SLED Awards search has no awardee filter — so it falls through to unmapped.
const SLED_AWARDS_MULTI_SELECT_KEYS = ['state'];

// Gateway sends bounds as `minValue`/`maxValue`; the screen's filter field is `awardValue`.
const SLED_AWARDS_NUMBER_SINGLE_KEYS = [
  { source: 'minValue', field: 'awardValue', operator: IS_GREATER },
  { source: 'maxValue', field: 'awardValue', operator: IS_LESS },
];

const SLED_AWARDS_CHIP_LABELS = {
  state: 'State',
  awardValue: 'Award value',
  text: 'Keyword',
};

// SLED (state & local) Contract Opportunities. The gateway sends `naics`/`psc` (PSC codes), but
// the screen fields are `naicsCategory`/`pscCategory`, so those are source-remapped. `state`/
// `county` are safe standard multi-selects here: county's `whereFrom` only scopes its
// autocomplete options, it does not gate the value (unlike the Vendors `country`
// availableWhen/clearWhenUnavailable case).
//
// Silently ignored (no user-facing filter): `activeOnly` (implicit request param the backend
// defaults to true) and `opportunityType` (the Contract/Forecast discriminator the backend
// derives itself). These are consumed so they don't clutter the "parts we could not map" panel.
//
// `productServiceCategory` is a plain-English category LABEL (e.g. "Construction", "Information
// Technology"), not a PSC code. The screen's pscCategory filter is code-based, so labels are folded
// into quoted `filters.text` (exact-phrase keyword) in mapWithConfig for every lane — see
// mergeProductServiceCategoryIntoText.
const SLED_OPP_DATE_PAIR_KEYS = {
  postedFrom: { field: 'postedDate', bound: 'from' },
  postedTo: { field: 'postedDate', bound: 'to' },
  dueFrom: { field: 'dueDate', bound: 'from' },
  dueTo: { field: 'dueDate', bound: 'to' },
  updatedFrom: { field: 'lastUpdated', bound: 'from' },
  updatedTo: { field: 'lastUpdated', bound: 'to' },
  modifiedFrom: { field: 'lastUpdated', bound: 'from' },
  modifiedTo: { field: 'lastUpdated', bound: 'to' },
};

const SLED_OPP_MULTI_SELECT_KEYS = [
  'state',
  'county',
  'agency',
  'agencyCategorization',
  { source: 'naics', field: 'naicsCategory' },
  { source: 'psc', field: 'pscCategory' },
  'keywords',
];

const SLED_OPP_CHIP_LABELS = {
  state: 'State',
  county: 'County',
  agency: 'Agency',
  agencyCategorization: 'Agency type',
  naicsCategory: 'NAICS',
  pscCategory: 'PSC',
  keywords: 'Keyword',
  postedDate: 'Posted date',
  dueDate: 'Due date',
  lastUpdated: 'Last updated',
  text: 'Keyword',
};

const SLED_IGNORED_KEYS = ['activeOnly', 'opportunityType'];

// SLED (state & local) Forecasts. Structurally the twin of SLED Opportunities — same screen
// filter set (state/county/agency/agencyCategorization + naics/psc code fields), same silently
// ignored plumbing keys. productServiceCategory labels fold into quoted text via mapWithConfig.
// It reuses the Opportunities multi-select/chip/ignored config to avoid duplication.
//
// The one difference: the gateway expresses forecast timing as `forecastFrom`/`forecastTo`
// ("expected/anticipated" dates). The Forecasts screen has no dedicated forecast-date field,
// so these map onto the forward-looking `dueDate` filter (confirmed with David) alongside the
// standard posted/due/updated pairs.
//
// NOTE: NAICS/PSC currently arrive as gateway-side `unmapped` entries for this lane rather than
// `naics`/`psc` filters; the API team plans to emit them properly. The naics->naicsCategory and
// psc->pscCategory remaps are configured now via the shared Opportunities multi-select config,
// so they will apply automatically once the gateway starts sending them — no further change.
const SLED_FORECAST_DATE_PAIR_KEYS = {
  ...SLED_OPP_DATE_PAIR_KEYS,
  forecastFrom: { field: 'dueDate', bound: 'from' },
  forecastTo: { field: 'dueDate', bound: 'to' },
};

// Federal Agents (/federal-agents)
const FEDERAL_AGENTS_MULTI_SELECT_KEYS = [
  { source: 'naics', field: 'naicsCategory' },
  'naicsCategory',
  { source: 'agency', field: 'agencyId' },
  'agencyId',
  { source: 'office', field: 'officeId' },
  'officeId',
  'state',
  'keywords',
];

const FEDERAL_AGENTS_TEXT_SINGLE_KEYS = ['email'];

const FEDERAL_AGENTS_CHIP_LABELS = {
  naicsCategory: 'NAICS',
  agencyId: 'Agency',
  officeId: 'Office',
  state: 'State',
  email: 'Email',
  keywords: 'Keyword',
  text: 'Keyword',
};

// Federal Grant Opportunities (/grant-opportunities)
const FEDERAL_GRANTS_DATE_PAIR_KEYS = {
  dueFrom: { field: 'dueDate', bound: 'from' },
  dueTo: { field: 'dueDate', bound: 'to' },
  postedFrom: { field: 'postedDate', bound: 'from' },
  postedTo: { field: 'postedDate', bound: 'to' },
  modifiedFrom: { field: 'lastModifiedDate', bound: 'from' },
  modifiedTo: { field: 'lastModifiedDate', bound: 'to' },
  updatedFrom: { field: 'lastModifiedDate', bound: 'from' },
  updatedTo: { field: 'lastModifiedDate', bound: 'to' },
};

const FEDERAL_GRANTS_MULTI_SELECT_KEYS = [
  { source: 'applicantType', field: 'applicantTypes' },
  'applicantTypes',
  { source: 'grantProgram', field: 'grantPrograms' },
  'grantPrograms',
  'keywords',
];

const FEDERAL_GRANTS_TEXT_SINGLE_KEYS = ['solicitationNumber'];

const FEDERAL_GRANTS_CHIP_LABELS = {
  applicantTypes: 'Eligible applicants',
  grantPrograms: 'Grant program',
  solicitationNumber: 'Solicitation #',
  dueDate: 'Due date',
  postedDate: 'Posted date',
  lastModifiedDate: 'Last modified',
  keywords: 'Keyword',
  text: 'Keyword',
};

// Federal Agencies & Offices (/agencies-offices)
const FEDERAL_AGENCIES_MULTI_SELECT_KEYS = [
  { source: 'department', field: 'departmentId' },
  'departmentId',
  { source: 'agency', field: 'agencyId' },
  'agencyId',
  'state',
];

const FEDERAL_AGENCIES_CHIP_LABELS = {
  departmentId: 'Department',
  agencyId: 'Agency',
  state: 'State',
  text: 'Keyword',
};

const FEDERAL_AGENCIES_IGNORED_KEYS = ['type'];

// NSN Opportunities (/nsn-opportunities)
const NSN_OPP_DATE_PAIR_KEYS = {
  issuedFrom: { field: 'issuedDate', bound: 'from' },
  issuedTo: { field: 'issuedDate', bound: 'to' },
  returnByFrom: { field: 'returnByDate', bound: 'from' },
  returnByTo: { field: 'returnByDate', bound: 'to' },
  dueFrom: { field: 'returnByDate', bound: 'from' },
  dueTo: { field: 'returnByDate', bound: 'to' },
};

const NSN_OPP_MULTI_SELECT_KEYS = ['nsnPart', 'buyerCode', 'purchaseRequest', 'setAsideIndicator', 'keywords'];

const NSN_OPP_FOLD_KEYS_INTO_TEXT = ['nomenclature'];

const NSN_OPP_TEXT_SINGLE_KEYS = ['solicitationNumber'];

const NSN_OPP_CHIP_LABELS = {
  nsnPart: 'NSN / Part #',
  nomenclature: 'Nomenclature',
  buyerCode: 'Buyer code',
  purchaseRequest: 'Purchase request',
  setAsideIndicator: 'Set-aside indicator',
  solicitationNumber: 'Solicitation #',
  issuedDate: 'Issued date',
  returnByDate: 'Return by date',
  keywords: 'Keyword',
  text: 'Keyword',
};

function toOption(value, resolveOption) {
  if (value == null || value === '') return null;
  if (typeof resolveOption === 'function') {
    const resolved = resolveOption(value);
    if (resolved) return resolved;
  }
  if (typeof value === 'object') {
    const id = value.id ?? value.value ?? value.code ?? value.name;
    const label = value.value ?? value.name ?? value.label ?? value.id ?? String(id);
    if (id == null || id === '') return null;
    return { id, value: label };
  }
  return { id: value, value: String(value) };
}

function toMultiSelectField(values, resolveOption) {
  const list = (Array.isArray(values) ? values : [values])
    .map((value) => toOption(value, resolveOption))
    .filter(Boolean);
  if (!list.length) return null;
  return { type: ANY_OF_THOSE, data: list };
}

function formatDateChip(from, to) {
  if (from && to) return `${from} – ${to}`;
  if (from) return `from ${from}`;
  if (to) return `to ${to}`;
  return '';
}

function isAwardsType(values) {
  const list = Array.isArray(values) ? values : [values];
  return list.some((item) => {
    const raw = typeof item === 'object' ? item.value ?? item.name ?? item.id ?? '' : item;
    return String(raw).toLowerCase().includes('award');
  });
}

function parseBooleanFlag(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes'].includes(normalized)) return true;
    if (['false', '0', 'no'].includes(normalized)) return false;
  }
  return null;
}

/**
 * Rewrite backend "lane" jargon in customer-facing NL copy to "category".
 * Preserves capitalization (lane/Lane/lanes/Lanes).
 */
export function humanizeLaneWording(text) {
  if (text == null || text === '') return text == null ? text : '';
  return String(text).replaceAll(/\blanes?\b/gi, (match) => {
    const plural = match.toLowerCase().endsWith('s');
    const first = match.charAt(0);
    const capitalized = first === first.toUpperCase() && first !== first.toLowerCase();
    if (plural) return capitalized ? 'Categories' : 'categories';
    return capitalized ? 'Category' : 'category';
  });
}

/**
 * Normalize API unmapped entries (strings or { fragment, concept, message }) into display items.
 */
export function normalizeUnmappedItems(items = []) {
  return items
    .map((item) => {
      if (item == null || item === '') return null;
      if (typeof item === 'string') {
        const message = humanizeLaneWording(item);
        return { key: item, label: item, message };
      }
      if (typeof item === 'object') {
        const fragment = item.fragment || item.concept || item.key || '';
        const rawMessage = item.message || (fragment ? `Could not map “${fragment}”.` : '');
        const message = humanizeLaneWording(rawMessage);
        if (!message && !fragment) return null;
        return {
          key: fragment || message,
          label: fragment || null,
          message: message || fragment,
        };
      }
      return { key: String(item), label: String(item), message: String(item) };
    })
    .filter(Boolean);
}

function applyDatePairs(nlFilters, datePairKeys, chipLabels, fields, chips, consumed) {
  Object.entries(datePairKeys).forEach(([nlKey, { field, bound }]) => {
    if (nlFilters[nlKey] == null || nlFilters[nlKey] === '') return;
    consumed.add(nlKey);
    if (!fields[field]) {
      fields[field] = { type: IS_BETWEEN, from: undefined, to: undefined };
    }
    fields[field][bound] = nlFilters[nlKey];
  });

  Object.entries(fields).forEach(([field, value]) => {
    if (value.type === IS_BETWEEN) {
      const label = formatDateChip(value.from, value.to);
      if (label) {
        chips.push({ key: field, label: chipLabels[field] || field, value: label });
      }
    }
  });
}

/**
 * Normalize a multi-select config entry into { source, field }. Entries may be a plain string
 * (NL response key === target filter field) or a { source, field } object when they differ
 * (e.g. the SLED Opportunities gateway sends `naics`/`psc` but the screen fields are
 * `naicsCategory`/`pscCategory`).
 */
function normalizeMultiSelectEntry(entry) {
  if (typeof entry === 'string') return { source: entry, field: entry };
  return { source: entry.source, field: entry.field };
}

function applyMultiSelects(nlFilters, config, fields, chips, consumed) {
  config.multiSelectKeys.forEach((entry) => {
    const { source, field } = normalizeMultiSelectEntry(entry);
    if (nlFilters[source] == null) return;
    consumed.add(source);
    const resolveOption =
      typeof config.resolveMultiSelectOption === 'function'
        ? (value) => config.resolveMultiSelectOption(field, value)
        : undefined;
    const mapped = toMultiSelectField(nlFilters[source], resolveOption);
    if (!mapped) return;
    fields[field] = mapped;
    chips.push({
      key: field,
      label: config.chipLabels[field] || field,
      value: mapped.data.map((d) => d.value).join(', '),
    });
    if (config.detectQuickFilter) {
      config.detectQuickFilter(source, nlFilters[source]);
    }
  });
}

function applyHasSetAside(nlFilters, chipLabels, fields, chips, consumed) {
  if (nlFilters.hasSetAside == null || nlFilters.hasSetAside === '') return;
  consumed.add('hasSetAside');
  const flag = parseBooleanFlag(nlFilters.hasSetAside);
  if (flag === true) {
    fields.setAside = { type: EXISTS, data: [] };
    chips.push({ key: 'hasSetAside', label: chipLabels.hasSetAside, value: 'Required' });
  } else if (flag === false) {
    fields.setAside = { type: DOES_NOT_EXISTS, data: [] };
    chips.push({ key: 'hasSetAside', label: chipLabels.hasSetAside, value: 'None / unrestricted' });
  }
}

function parseNumericValue(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const cleaned = value.replaceAll(/[$,\s]/g, '');
    if (cleaned === '') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatCurrencyChip(amount, operator = IS_GREATER) {
  const formatted = `$${amount.toLocaleString('en-US')}`;
  if (operator === IS_LESS) return `under ${formatted}`;
  return `over ${formatted}`;
}

function formatCurrencyRangeChip(from, to) {
  return `$${from.toLocaleString('en-US')} – $${to.toLocaleString('en-US')}`;
}

/**
 * Map bare number amounts onto NUMBER / AMOUNT_RANGE filters. Opt-in per lane via
 * `config.numberSingleKeys`; lanes without it are unaffected.
 *
 * Each entry is either:
 *   - a string, when the NL response key and the target filter field are the same
 *     (e.g. 'dollarsObligated') — defaults to IS_GREATER ("over"), or
 *   - a { source, field, operator? } object, when they differ or the operator is not
 *     "over" (e.g. SLED Awards `minValue` → awardValue IS_GREATER, `maxValue` →
 *     awardValue IS_LESS).
 *
 * When multiple entries target the same field (min + max), they merge into IS_BETWEEN.
 * The chip label is keyed by the target field.
 */
function applyNumberSingles(nlFilters, config, fields, chips, consumed) {
  if (!Array.isArray(config.numberSingleKeys)) return;

  // Collect per target field so minValue + maxValue on the same field can become IS_BETWEEN.
  const pending = {};

  config.numberSingleKeys.forEach((entry) => {
    const source = typeof entry === 'string' ? entry : entry.source;
    const field = typeof entry === 'string' ? entry : entry.field;
    const operator = typeof entry === 'string' ? IS_GREATER : entry.operator || IS_GREATER;
    if (nlFilters[source] == null || nlFilters[source] === '') return;
    consumed.add(source);
    const amount = parseNumericValue(nlFilters[source]);
    if (amount == null) return;

    if (!pending[field]) pending[field] = { from: null, to: null };
    if (operator === IS_LESS) {
      pending[field].to = amount;
    } else {
      pending[field].from = amount;
    }
  });

  Object.entries(pending).forEach(([field, { from, to }]) => {
    const label = config.chipLabels[field] || field;
    if (from != null && to != null) {
      fields[field] = { type: IS_BETWEEN, data: '', from: String(from), to: String(to) };
      chips.push({ key: field, label, value: formatCurrencyRangeChip(from, to) });
      return;
    }
    if (to != null) {
      const data = String(to);
      fields[field] = { type: IS_LESS, data, from: '', to: data };
      chips.push({ key: field, label, value: formatCurrencyChip(to, IS_LESS) });
      return;
    }
    const data = String(from);
    fields[field] = { type: IS_GREATER, data, from: '', to: '' };
    chips.push({ key: field, label, value: formatCurrencyChip(from, IS_GREATER) });
  });
}

function normalizeTextSingleEntry(entry) {
  if (typeof entry === 'string') return { source: entry, field: entry };
  return {
    source: entry.source || entry.field,
    field: entry.field || entry.source,
  };
}

function extractFirstString(value) {
  if (Array.isArray(value)) {
    return value[0] == null ? '' : String(value[0]);
  }
  return value == null ? '' : String(value);
}

function applyTextSingles(nlFilters, config, fields, chips, consumed) {
  if (!Array.isArray(config.textSingleKeys)) return;

  config.textSingleKeys.forEach((entry) => {
    const { source, field } = normalizeTextSingleEntry(entry);
    consumed.add(source);
    const raw = nlFilters[source];
    if (raw == null || raw === '') return;

    const trimmed = extractFirstString(raw).trim();
    if (!trimmed) return;

    fields[field] = { type: ONLY_TEXT, data: trimmed };

    const label = config.chipLabels?.[field] || config.chipLabels?.[source] || field;
    chips.push({ key: field, label, value: trimmed });
  });
}

/**
 * Silently consume keys that the NL service emits but that have no user-facing filter on the
 * screen (e.g. request-level plumbing like `activeOnly`, or the `opportunityType` discriminator
 * the backend derives on its own). Marking them consumed keeps them out of the "parts we could
 * not map" panel without applying any filter. Opt-in per lane via `config.ignoredKeys`.
 */
function applyIgnoredKeys(config, consumed) {
  if (!Array.isArray(config.ignoredKeys)) return;
  config.ignoredKeys.forEach((key) => consumed.add(key));
}

/**
 * Quote a product/service category label for exact-phrase keyword search.
 * Labels that are already wrapped in matching double quotes are left unchanged.
 */
function toQuotedPhrase(label) {
  const trimmed = String(label).trim();
  if (!trimmed) return '';
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed;
  }
  const escapedQuote = String.raw`\"`;
  const escaped = trimmed.replaceAll('"', escapedQuote);
  return `"${escaped}"`;
}

/**
 * Fold gateway `productServiceCategory` labels into the search-bar keyword as quoted phrases.
 * Consumes the key so it does not appear in the unmapped panel. Does not touch pscCategory.
 */
function mergeProductServiceCategoryIntoText(nlFilters, baseText, consumed) {
  if (nlFilters.productServiceCategory == null || nlFilters.productServiceCategory === '') {
    return baseText == null ? '' : String(baseText);
  }

  consumed.add('productServiceCategory');

  const labels = toValueList(nlFilters.productServiceCategory)
    .map((value) => {
      if (value == null || value === '') return '';
      if (typeof value === 'object') {
        return String(value.value ?? value.name ?? value.label ?? value.id ?? '').trim();
      }
      return String(value).trim();
    })
    .filter(Boolean);

  let text = baseText == null ? '' : String(baseText);

  labels.forEach((label) => {
    const quoted = toQuotedPhrase(label);
    if (!quoted) return;
    const textLower = text.toLowerCase();
    const alreadyPresent = textLower.includes(quoted.toLowerCase()) || textLower.includes(label.toLowerCase());
    if (alreadyPresent) return;
    text = text ? `${text} ${quoted}` : quoted;
  });

  return text;
}

/**
 * Fold resolved `lookups` ({ param, text }) into a working copy of the raw filters.
 * The external service delivers some values (e.g. resolved NAICS codes or canonical
 * set-aside labels) via `lookups` rather than `filters`. Only entries whose `param`
 * matches a known field for this lane are merged; anything else is left untouched so
 * it remains available as passthrough metadata on the response.
 */
function toValueList(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function mergeLookupsIntoFilters(nlFilters, lookups, knownKeys) {
  if (!Array.isArray(lookups) || !lookups.length) return nlFilters;

  const merged = { ...nlFilters };

  lookups.forEach((lookup) => {
    const param = lookup?.param;
    const text = lookup?.text;
    if (!param || !knownKeys.has(param) || text == null || text === '') return;

    const existingList = toValueList(merged[param]);
    merged[param] = existingList.includes(text) ? existingList : [...existingList, text];
  });

  return merged;
}

function collectUnmapped(response, nlFilters, consumed) {
  const unmapped = normalizeUnmappedItems(response?.unmapped || []);
  Object.keys(nlFilters).forEach((key) => {
    if (consumed.has(key)) return;
    const value = nlFilters[key];
    if (value == null || value === '') return;
    unmapped.push({
      key,
      label: key,
      message: `Filter “${key}” is not supported on this search yet.`,
    });
  });
  return unmapped;
}

/**
 * Test whether a string is a 2-6 digit NAICS code or code prefix.
 */
export function isNumericNaicsCode(value) {
  if (value == null) return false;
  const str = String(value).trim();
  return /^\d{2,6}$/.test(str);
}

/**
 * Extract non-numeric NAICS labels (e.g. "IT services", "Construction") from `naics`
 * or `naicsCategory` filters/lookups. The screen's NAICS filter is strictly code-based
 * (Elasticsearch indexes numeric codes), so text labels cannot match there.
 *
 * Any valid numeric codes are preserved in `nlFilters`. If all values are non-numeric,
 * the key is removed from `nlFilters` and marked `consumed` so it does not fall into unmapped.
 * The non-numeric labels are returned to be folded into `filters.text` (search bar keywords),
 * where backend searches (such as naicsKeywordMapper) automatically resolve them.
 */
export function extractNonNumericNaics(nlFilters, consumed) {
  const naicsKeys = ['naics', 'naicsCategory'];
  const nonNumericLabels = [];

  naicsKeys.forEach((key) => {
    if (nlFilters[key] == null) return;
    const items = toValueList(nlFilters[key]);
    const numericItems = [];

    items.forEach((item) => {
      if (item == null) return;
      let rawCode = '';
      let displayLabel = '';

      if (typeof item === 'object') {
        rawCode = item.id ?? item.code ?? item.value ?? '';
        displayLabel = item.name ?? item.label ?? item.title ?? rawCode;
      } else {
        rawCode = String(item);
        displayLabel = String(item);
      }

      const strCode = String(rawCode).trim();
      const strLabel = String(displayLabel).trim();

      if (isNumericNaicsCode(strCode)) {
        numericItems.push(item);
      } else if (strLabel || strCode) {
        nonNumericLabels.push(strLabel || strCode);
      }
    });

    if (numericItems.length > 0) {
      nlFilters[key] = numericItems;
    } else {
      delete nlFilters[key];
      consumed.add(key);
    }
  });

  return nonNumericLabels;
}

/**
 * Shared lane-agnostic mapping from an NL interpret response to the app's filter payload.
 * Lane specifics (field keys, chip labels, quick-filter detection) are supplied via config.
 */
function mapWithConfig(response, config) {
  const rawFilters = response?.filters || {};
  // Only multi-select and text-single fields are delivered as resolved lookups ({ param, text }).
  // Date ranges and boolean flags always arrive directly in `filters`. Lookups are keyed by
  // the NL response (source) key, so resolve entries to their source before building the set.
  const multiSelectMergeKeys = Array.isArray(config.multiSelectKeys)
    ? config.multiSelectKeys.map((entry) => normalizeMultiSelectEntry(entry).source)
    : [];
  const textSingleMergeKeys = Array.isArray(config.textSingleKeys)
    ? config.textSingleKeys.map((entry) => normalizeTextSingleEntry(entry).source)
    : [];
  const foldMergeKeys = Array.isArray(config.foldKeysIntoText) ? config.foldKeysIntoText : [];
  const lookupMergeKeys = new Set([...multiSelectMergeKeys, ...textSingleMergeKeys, ...foldMergeKeys]);
  const nlFilters = mergeLookupsIntoFilters(rawFilters, response?.lookups, lookupMergeKeys);
  const fields = {};
  const chips = [];
  const consumed = new Set();

  const nonNumericNaicsLabels = extractNonNumericNaics(nlFilters, consumed);

  applyDatePairs(nlFilters, config.datePairKeys, config.chipLabels, fields, chips, consumed);
  applyMultiSelects(nlFilters, config, fields, chips, consumed);
  applyTextSingles(nlFilters, config, fields, chips, consumed);
  applyHasSetAside(nlFilters, config.chipLabels, fields, chips, consumed);
  applyNumberSingles(nlFilters, config, fields, chips, consumed);
  applyIgnoredKeys(config, consumed);

  const baseText = response?.q == null ? '' : String(response.q);
  let text = mergeProductServiceCategoryIntoText(nlFilters, baseText, consumed);

  if (Array.isArray(config.foldKeysIntoText)) {
    config.foldKeysIntoText.forEach((key) => {
      if (nlFilters[key] == null || nlFilters[key] === '') return;
      consumed.add(key);
      const labels = toValueList(nlFilters[key])
        .map((value) => {
          if (value == null || value === '') return '';
          if (typeof value === 'object') {
            return String(value.value ?? value.name ?? value.label ?? value.id ?? '').trim();
          }
          return String(value).trim();
        })
        .filter(Boolean);

      labels.forEach((label) => {
        const textLower = text.toLowerCase();
        const labelLower = label.toLowerCase();
        if (!textLower.includes(labelLower)) {
          text = text ? `${text} ${label}` : label;
        }
      });
    });
  }

  nonNumericNaicsLabels.forEach((label) => {
    const textLower = text.toLowerCase();
    const labelLower = label.toLowerCase();
    if (!textLower.includes(labelLower)) {
      text = text ? `${text} ${label}` : label;
    }
  });

  const unmapped = collectUnmapped(response, nlFilters, consumed);

  if (text) {
    chips.unshift({ key: 'text', label: config.chipLabels.text, value: text });
  }

  return {
    answerable: response?.answerable !== false,
    interpretation: humanizeLaneWording(response?.interpretation || '') || '',
    query: text,
    unmapped,
    lookups: response?.lookups || [],
    chips,
    quickFilter: config.getQuickFilter ? config.getQuickFilter() : null,
    filters: {
      reset: true,
      text,
      ...fields,
    },
  };
}

function mapFcoFilters(response) {
  let quickFilter = null;
  return mapWithConfig(response, {
    datePairKeys: FCO_DATE_PAIR_KEYS,
    multiSelectKeys: FCO_MULTI_SELECT_KEYS,
    chipLabels: FCO_CHIP_LABELS,
    detectQuickFilter: (key, value) => {
      if (key === 'type' && isAwardsType(value)) {
        quickFilter = 'awards';
      }
    },
    getQuickFilter: () => quickFilter,
  });
}

function mapVendorFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: VENDOR_DATE_PAIR_KEYS,
    multiSelectKeys: VENDOR_MULTI_SELECT_KEYS,
    chipLabels: VENDOR_CHIP_LABELS,
    resolveMultiSelectOption: resolveVendorFilterOption,
  });
}

function mapAwardsFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: AWARDS_DATE_PAIR_KEYS,
    multiSelectKeys: AWARDS_MULTI_SELECT_KEYS,
    numberSingleKeys: AWARDS_NUMBER_SINGLE_KEYS,
    chipLabels: AWARDS_CHIP_LABELS,
  });
}

function mapSledAwardsFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: {},
    multiSelectKeys: SLED_AWARDS_MULTI_SELECT_KEYS,
    numberSingleKeys: SLED_AWARDS_NUMBER_SINGLE_KEYS,
    chipLabels: SLED_AWARDS_CHIP_LABELS,
  });
}

function mapSledOpportunitiesFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: SLED_OPP_DATE_PAIR_KEYS,
    multiSelectKeys: SLED_OPP_MULTI_SELECT_KEYS,
    chipLabels: SLED_OPP_CHIP_LABELS,
    ignoredKeys: SLED_IGNORED_KEYS,
  });
}

// Forecasts reuses the Opportunities multi-select/chip/ignored config (identical filter set);
// only the date-pair map differs (adds forecastFrom/To -> dueDate).
function mapSledForecastsFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: SLED_FORECAST_DATE_PAIR_KEYS,
    multiSelectKeys: SLED_OPP_MULTI_SELECT_KEYS,
    chipLabels: SLED_OPP_CHIP_LABELS,
    ignoredKeys: SLED_IGNORED_KEYS,
  });
}

function mapFederalAgentsFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: {},
    multiSelectKeys: FEDERAL_AGENTS_MULTI_SELECT_KEYS,
    textSingleKeys: FEDERAL_AGENTS_TEXT_SINGLE_KEYS,
    chipLabels: FEDERAL_AGENTS_CHIP_LABELS,
  });
}

function mapGrantOpportunitiesFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: FEDERAL_GRANTS_DATE_PAIR_KEYS,
    multiSelectKeys: FEDERAL_GRANTS_MULTI_SELECT_KEYS,
    textSingleKeys: FEDERAL_GRANTS_TEXT_SINGLE_KEYS,
    chipLabels: FEDERAL_GRANTS_CHIP_LABELS,
  });
}

function extractFederalAgencyTabType(response) {
  const rawType = response?.filters?.type;
  if (rawType != null) {
    const first = Array.isArray(rawType) ? rawType[0] : rawType;
    const str = typeof first === 'object' && first !== null ? first.id ?? first.value ?? '' : String(first);
    const normalized = String(str).trim().toLowerCase();
    if (['department', 'agency', 'office'].includes(normalized)) {
      return normalized;
    }
  }

  const lookupType = (response?.lookups || []).find((l) => l.param === 'type')?.text;
  if (lookupType != null) {
    const normalized = String(lookupType).trim().toLowerCase();
    if (['department', 'agency', 'office'].includes(normalized)) {
      return normalized;
    }
  }

  return null;
}

function mapFederalAgenciesFilters(response) {
  const tabType = extractFederalAgencyTabType(response);
  return mapWithConfig(response, {
    datePairKeys: {},
    multiSelectKeys: FEDERAL_AGENCIES_MULTI_SELECT_KEYS,
    chipLabels: FEDERAL_AGENCIES_CHIP_LABELS,
    ignoredKeys: FEDERAL_AGENCIES_IGNORED_KEYS,
    getQuickFilter: () => tabType,
  });
}

function mapNsnOpportunitiesFilters(response) {
  return mapWithConfig(response, {
    datePairKeys: NSN_OPP_DATE_PAIR_KEYS,
    multiSelectKeys: NSN_OPP_MULTI_SELECT_KEYS,
    textSingleKeys: NSN_OPP_TEXT_SINGLE_KEYS,
    chipLabels: NSN_OPP_CHIP_LABELS,
    foldKeysIntoText: NSN_OPP_FOLD_KEYS_INTO_TEXT,
  });
}

const LANE_MAPPERS = {
  [NL_LANES.FEDERAL_CONTRACT_OPPORTUNITIES]: mapFcoFilters,
  [NL_LANES.VENDORS]: mapVendorFilters,
  [NL_LANES.FEDERAL_CONTRACT_AWARDS]: mapAwardsFilters,
  [NL_LANES.SLED_AWARDS]: mapSledAwardsFilters,
  [NL_LANES.SLED_OPPORTUNITIES]: mapSledOpportunitiesFilters,
  [NL_LANES.SLED_FORECASTS]: mapSledForecastsFilters,
  [NL_LANES.FEDERAL_AGENTS]: mapFederalAgentsFilters,
  [NL_LANES.FEDERAL_GRANT_OPPORTUNITIES]: mapGrantOpportunitiesFilters,
  [NL_LANES.FEDERAL_AGENCIES]: mapFederalAgenciesFilters,
  [NL_LANES.NSN_OPPORTUNITIES]: mapNsnOpportunitiesFilters,
};

/**
 * Convert an NL interpret API response into list-search filter payload + UI metadata.
 * @param {string} lane
 * @param {object} response
 */
export function mapNlResponseToFilters(lane, response) {
  const mapper = LANE_MAPPERS[lane];
  if (!mapper) {
    return {
      answerable: false,
      interpretation: '',
      query: response?.q || '',
      unmapped: normalizeUnmappedItems(['unsupported-lane']),
      lookups: [],
      chips: [],
      quickFilter: null,
      filters: { reset: true, text: response?.q || '' },
    };
  }
  return mapper(response);
}
